import * as React from 'react';
import { useSelector } from 'react-redux';
import { artikelGruppenByNrSelector, einheitenByCodeSelector } from 'store/lookups/selector';
import { BestellungPosition } from 'api/bestellungPosition';
import classnames from 'classnames';
import { formatDecimal } from 'utils/formatter';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { formStyles } from 'styles/formStyles';
import { FormDecimalField } from 'formComponents/FormDecimalField';
import { LieferArt } from 'formComponents/lookups/LieferArt';

interface BestellungPositionenProps {
    bestellungPositionen: BestellungPosition[];
    onChange: (newTransferPositionen: BestellungPosition[]) => void;
    withLArt: boolean;
}

export const BestellungPositionenSelect: React.FC<BestellungPositionenProps> = ({
    bestellungPositionen,
    onChange,
    withLArt,
}) => {
    const artikelGruppenByNr = useSelector(artikelGruppenByNrSelector);
    const einheitenByCode = useSelector(einheitenByCodeSelector);

    return (
        <div>
            <h3>Bitte wählen Sie die zu übernehmenden Positionen und Mengen</h3>
            <table className={formStyles.positionenTable}>
                <thead className={formStyles.positionenTable}>
                    <tr>
                        <td />
                        <td>Pos.</td>
                        <td>Art. Grp.</td>
                        <td>Art. Nr.</td>
                        <td>Text</td>
                        <td className={formStyles.positionenEinhCell}>Einh</td>
                        <td className={formStyles.positionenMoneyCell}>Menge</td>
                        <td className={formStyles.positionenMoneyCell}>Offen</td>
                        <td className={formStyles.transferMengeHeader}>
                            Menge
                            <br />
                            erhalten
                        </td>
                        {withLArt && <td className={formStyles.transferLArt}>LArt</td>}
                    </tr>
                </thead>
                <tbody>
                    {bestellungPositionen.map((bestellungPosition) => (
                        <tr key={bestellungPosition.pos} className={formStyles.positionenRow}>
                            <td>
                                <Checkbox
                                    className={formStyles.checkbox}
                                    checked={bestellungPosition.transfer}
                                    onChange={(event, checked) => {
                                        onChange(
                                            bestellungPositionen.map((ap) =>
                                                ap.pos === bestellungPosition.pos
                                                    ? {
                                                          ...bestellungPosition,
                                                          transfer: checked,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            <td className={classnames(formStyles.positionenCell)}>{bestellungPosition.pos}</td>
                            <td className={classnames(formStyles.positionenCell)}>
                                {(artikelGruppenByNr[bestellungPosition.artikelGruppeNr] &&
                                    artikelGruppenByNr[bestellungPosition.artikelGruppeNr]?.text) ||
                                    bestellungPosition.artikelGruppeNr}
                            </td>
                            <td className={classnames(formStyles.positionenCell)}>
                                <span>{bestellungPosition.artikelNr}</span>
                            </td>
                            <td className={classnames(formStyles.positionenCell)}>
                                {bestellungPosition.posText}
                                {bestellungPosition.posBem ? (
                                    <i>
                                        <br />
                                        {bestellungPosition.posBem}
                                    </i>
                                ) : (
                                    ''
                                )}
                            </td>
                            <td className={classnames(formStyles.positionenCell)}>
                                {bestellungPosition.posEinh
                                    ? einheitenByCode[bestellungPosition.posEinh].text || bestellungPosition.posEinh
                                    : '?'}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(bestellungPosition.posMenge, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMengeCell)}>
                                {formatDecimal(bestellungPosition.offen, 3)}
                            </td>
                            <td className={classnames(formStyles.positionenCell, formStyles.positionenMoneyCell)}>
                                <FormDecimalField
                                    options={{
                                        disabled: !bestellungPosition.transfer,
                                        fixed: 3,
                                    }}
                                    label=""
                                    value={bestellungPosition.transferMenge || 0}
                                    onChange={(newValue): void => {
                                        onChange(
                                            bestellungPositionen.map((ap) =>
                                                ap.pos === bestellungPosition.pos
                                                    ? {
                                                          ...bestellungPosition,
                                                          transferMenge: newValue,
                                                      }
                                                    : ap,
                                            ),
                                        );
                                    }}
                                />
                            </td>
                            {withLArt && (
                                <td className={classnames(formStyles.positionenCell, formStyles.transferLArt)}>
                                    <LieferArt
                                        allowEmpty
                                        disabled={!bestellungPosition.transfer}
                                        value={(bestellungPosition?.transferLArt as string) || ''}
                                        menuPlacement="top"
                                        renderLabel="no"
                                        onChange={(newValue) => {
                                            onChange(
                                                bestellungPositionen.map((ap) =>
                                                    ap.pos === bestellungPosition.pos
                                                        ? {
                                                              ...bestellungPosition,
                                                              transferLArt: newValue,
                                                          }
                                                        : ap,
                                                ),
                                            );
                                        }}
                                    />
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
